import { useApolloClient } from "@apollo/client/react";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { RepeatIcon } from "lucide-react";
import { usePlaidLink } from "react-plaid-link";

import { Buttons } from "@/components/atoms/Buttons";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Mutations } from "@/constants";
import { setHotkeysSuspended } from "@/hooks/useContextTree";
import { State } from "@/state";
import { Typography } from "@/components/ui/typography";

import { Spinner } from "@/components/ui/spinner";

function openPlaidLink(open: () => void) {
  setHotkeysSuspended(true);
  (document.activeElement as HTMLElement | null)?.blur?.();
  open();
}

// Mirrors the existing ``ConnectBankAccountCommand`` flow, but instead
// of (1) creating a fresh link token, (2) opening Plaid Link in
// connect mode, and (3) exchanging a public token, this command:
//
//   1. Calls ``repairBankConnection`` to mint an *update-mode* link
//      token bound to the existing Item's ``access_token``.
//   2. Opens Plaid Link with that token. Plaid walks the user through
//      whatever broke the Item — credentials, MFA, OAuth re-consent —
//      and updates the existing ``access_token`` in place. **No new
//      Item is created.**
//   3. Closes the dialog on success. The cleanup (clearing
//      ``RECONNECT_REQUIRED`` and republishing a fresh Plaid sync)
//      happens server-side via Plaid's ``ITEM: LOGIN_REPAIRED``
//      webhook, which is why there's no follow-up exchange or commit
//      mutation here.
//
// The ``onExit`` path leaves ``sync_state`` alone — the user bailing
// out of the picker doesn't change the Item's status; the chip stays
// "Reconnection needed" and they can try again later.
export const COMMAND_ID = "reconnect-bank-account";

export type ReconnectBankAccountContextType = {
  financialInstitutionIdentifier: string;
  institutionName?: string | null;
};

function Component({
  context,
}: {
  commandInstanceId: string;
  context?: ReconnectBankAccountContextType;
}) {
  const apolloClient = useApolloClient();
  const clearCommandStack = State((store) => store.clearCommandStack);
  const setSnackbarMessage = State((store) => store.setSnackbarMessage);

  const financialInstitutionIdentifier =
    context?.financialInstitutionIdentifier;
  const institutionName = context?.institutionName ?? null;

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState<boolean>(true);
  const [isFinished, setIsFinished] = useState<boolean>(false);

  const generateToken = useCallback(async () => {
    if (!financialInstitutionIdentifier) return;
    setTokenLoading(true);
    try {
      const result = await apolloClient.mutate({
        mutation: Mutations.REPAIR_BANK_CONNECTION,
        variables: {
          input: { financialInstitutionIdentifier },
        },
      });
      // Apollo Client v4 types the mutation result as ``unknown`` until
      // a generic param is supplied; codegen'd ``Mutations.*`` documents
      // carry their own response type via TypedDocumentNode but the
      // ``mutate({...})`` overload doesn't pick it up in this codebase.
      // Cast to the codegen shape so we can safely walk the nested
      // payload.
      const data = result.data as
        | {
            repairBankConnection?: {
              data?: { linkToken?: string | null } | null;
              errors?: { errors?: Array<{ message: string }> } | null;
            };
          }
        | null
        | undefined;
      const payload = data?.repairBankConnection;
      const apiErrors = payload?.errors?.errors;
      if (apiErrors && apiErrors.length > 0) {
        throw new Error(apiErrors[0].message ?? "Reconnect failed");
      }
      const token = payload?.data?.linkToken;
      if (!token) {
        throw new Error("Server did not return a link token");
      }
      setLinkToken(token);
    } catch (error) {
      setSnackbarMessage(
        `Couldn't start reconnect: ${
          error instanceof Error ? error.message : String(error)
        }.`,
      );
      clearCommandStack();
    } finally {
      setTokenLoading(false);
    }
  }, [
    apolloClient,
    financialInstitutionIdentifier,
    setSnackbarMessage,
    clearCommandStack,
  ]);

  useEffect(() => {
    void generateToken();
  }, [generateToken]);

  const onSuccess = useCallback(() => {
    // Plaid Link succeeded -- nothing for us to call. Plaid will fire
    // ``ITEM: LOGIN_REPAIRED`` to our webhook which clears the chip
    // server-side and republishes a fresh sync. Mirror the user
    // confirmation here so the dialog visibly transitions instead of
    // closing immediately on a possibly-imperceptible flash.
    setIsFinished(true);
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: () => {
      // User bailed out (or hit an in-Link error). Leave server state
      // alone -- chip remains "Reconnection needed" so they can retry.
      setHotkeysSuspended(false);
      if (!isFinished) clearCommandStack();
    },
  });

  useEffect(() => {
    return () => {
      setHotkeysSuspended(false);
    };
  }, []);

  return (
    <StickyLayout
      stickyBottomChildren={
        <Buttons
          buttons={
            !isFinished
              ? [
                  {
                    variant: "outline",
                    children: "Cancel",

                    onClick: () => {
                      setHotkeysSuspended(false);
                      clearCommandStack();
                    },
                  },
                  {
                    variant: "default",
                    children: (
                      <>{(!ready || tokenLoading) && <Spinner />}Reconnect</>
                    ),
                    disabled: !ready || tokenLoading,
                    onClick: () => openPlaidLink(() => open()),
                  },
                ]
              : [
                  {
                    variant: "default",
                    children: "Done",

                    onClick: () => {
                      setIsFinished(false);
                      setHotkeysSuspended(false);
                      clearCommandStack();
                    },
                  },
                ]
          }
        />
      }
    >
      <Typography level="body-md" className="p-4">
        {isFinished
          ? `Reconnected${institutionName ? ` to ${institutionName}` : ""}. Latest transactions will sync automatically.`
          : `Your bank connection${institutionName ? ` to ${institutionName}` : ""} needs to be refreshed. On the next screen Plaid will guide you through whatever's required — credentials, MFA, or a re-authorization prompt — to restore the link.`}
      </Typography>
    </StickyLayout>
  );
}

export function ReconnectBankAccountCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Reconnect bank account",
      getLabel: () => "Reconnect",
      description: "Reconnect a bank account whose Plaid Item needs attention",
      component: ({ commandInstanceId, context }) => (
        <Component
          commandInstanceId={commandInstanceId}
          context={context as ReconnectBankAccountContextType}
        />
      ),
      ReactIcon: RepeatIcon,
      group: "Integrations",
    });
    return () => unregisterCommandDefinition(COMMAND_ID);
  }, []);

  return null;
}
